import type { StatusResponse } from '@/pages/api/cross-chain/status';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import type { CrossChainStatusResponse } from '@/lib/api/0x-cross-chain/types';
import { ClientDataError, ClientNetworkError } from '@/lib/errors/client';

import { ZERO_X_CROSS_CHAIN_ERROR } from '@/constants/errors';

import { objectToQueryString } from '@/utils/string';

/**
 * Parameters for the 0x cross-chain status hook.
 */
export interface CrossChainStatusParams {
  /** Origin chain ID. */
  originChain: number | string;
  /** Origin transaction hash. */
  originTxHash: string;
  /** Quote ID from the quote response (optional but recommended). */
  quoteId?: string;
}

/**
 * Fetcher for 0x cross-chain status.
 */
const fetcher = async (
  params: CrossChainStatusParams,
): Promise<CrossChainStatusResponse | undefined> => {
  const response = await fetch('/api/cross-chain/status' + objectToQueryString(params));

  if (!response.ok) {
    try {
      const { reason } = await response.json();
      if (reason) throw new ClientNetworkError(reason);
    } catch (err) {
      if (err instanceof ClientNetworkError) throw err;
      throw new ClientNetworkError(ZERO_X_CROSS_CHAIN_ERROR.FAILED_TO_FETCH_STATUS);
    }
  }

  try {
    const data: StatusResponse = await response.json();
    if (!data.success) throw new ClientDataError(ZERO_X_CROSS_CHAIN_ERROR.FAILED_TO_FETCH_STATUS);
    return data.result;
  } catch (err) {
    if (err instanceof ClientDataError || err instanceof ClientNetworkError) throw err;
    throw new ClientDataError(ZERO_X_CROSS_CHAIN_ERROR.FAILED_TO_FETCH_STATUS);
  }
};

export type CrossChainStatusQueryOptions = Omit<
  UseQueryOptions<CrossChainStatusResponse | undefined, Error>,
  'queryKey' | 'queryFn' | 'enabled'
>;

export const crossChainStatusQueryKey = (params: CrossChainStatusParams | undefined) =>
  ['cross-chain-status', params] as const;

/**
 * Hook to fetch 0x cross-chain transaction status.
 *
 * @param params - Status request parameters. Pass `undefined` to disable the fetch.
 * @param options - React Query configuration options.
 */
export const useCrossChainStatus = (
  params: CrossChainStatusParams | undefined,
  options?: CrossChainStatusQueryOptions,
): UseQueryResult<CrossChainStatusResponse | undefined, Error> =>
  useQuery<CrossChainStatusResponse | undefined, Error>({
    queryKey: crossChainStatusQueryKey(params),
    queryFn: () => fetcher(params!),
    enabled: !!params,
    refetchInterval: 3_000,
    ...options,
  });
